import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { createTheme, Theme } from '@mui/material/styles';

type ThemeMode = 'light' | 'dark';

interface ThemeContextType {
  mode: ThemeMode;
  toggleTheme: () => void;
  setMode: (mode: ThemeMode) => void;
  theme: Theme;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// Shared typography, shape, and component overrides
const getThemeConfig = (mode: ThemeMode) => {
  const isDark = mode === 'dark';

  return createTheme({
    palette: {
      mode,
      primary: {
        main: '#6366f1',
        light: '#818cf8',
        dark: '#4f46e5',
        contrastText: '#ffffff'
      },
      secondary: {
        main: '#ec4899',
        light: '#f472b6',
        dark: '#db2777',
        contrastText: '#ffffff'
      },
      error: {
        main: '#ef4444',
        light: '#f87171',
        dark: '#dc2626'
      },
      warning: {
        main: '#f59e0b',
        light: '#fbbf24',
        dark: '#d97706'
      },
      info: {
        main: '#3b82f6',
        light: '#60a5fa',
        dark: '#2563eb'
      },
      success: {
        main: '#10b981',
        light: '#34d399',
        dark: '#059669'
      },
      background: {
        default: isDark ? '#000000' : '#f8fafc',
        paper: isDark ? '#0a0a0a' : '#ffffff'
      },
      text: {
        primary: isDark ? '#f1f5f9' : '#1e293b',
        secondary: isDark ? '#94a3b8' : '#64748b'
      },
      divider: isDark ? '#262626' : '#e2e8f0'
    },
    typography: {
      fontFamily: '"Inter", "Roboto", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      h1: { fontSize: '2.5rem', fontWeight: 700, letterSpacing: '-0.025em', lineHeight: 1.2 },
      h2: { fontSize: '2rem', fontWeight: 700, letterSpacing: '-0.025em', lineHeight: 1.3 },
      h3: { fontSize: '1.75rem', fontWeight: 600, letterSpacing: '-0.025em', lineHeight: 1.3 },
      h4: { fontSize: '1.5rem', fontWeight: 600, letterSpacing: '-0.025em', lineHeight: 1.4 },
      h5: { fontSize: '1.25rem', fontWeight: 600, letterSpacing: '-0.025em', lineHeight: 1.4 },
      h6: { fontSize: '1rem', fontWeight: 600, letterSpacing: '-0.025em', lineHeight: 1.5 },
      subtitle1: { fontSize: '1rem', fontWeight: 500, lineHeight: 1.5 },
      subtitle2: { fontSize: '0.875rem', fontWeight: 500, lineHeight: 1.5 },
      body1: { fontSize: '1rem', lineHeight: 1.6 },
      body2: { fontSize: '0.875rem', lineHeight: 1.6 },
      button: {
        fontSize: '0.875rem',
        fontWeight: 600,
        textTransform: 'none' as const,
        letterSpacing: '0.025em'
      }
    },
    shape: {
      borderRadius: 12
    },
    shadows: [
      'none',
      isDark ? '0px 1px 2px rgba(0, 0, 0, 0.3)' : '0px 1px 2px rgba(0, 0, 0, 0.05)',
      isDark
        ? '0px 1px 3px rgba(0, 0, 0, 0.4), 0px 1px 2px rgba(0, 0, 0, 0.3)'
        : '0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06)',
      isDark
        ? '0px 4px 6px -1px rgba(0, 0, 0, 0.4), 0px 2px 4px -1px rgba(0, 0, 0, 0.3)'
        : '0px 4px 6px -1px rgba(0, 0, 0, 0.1), 0px 2px 4px -1px rgba(0, 0, 0, 0.06)',
      isDark
        ? '0px 10px 15px -3px rgba(0, 0, 0, 0.4), 0px 4px 6px -2px rgba(0, 0, 0, 0.3)'
        : '0px 10px 15px -3px rgba(0, 0, 0, 0.1), 0px 4px 6px -2px rgba(0, 0, 0, 0.05)',
      isDark
        ? '0px 20px 25px -5px rgba(0, 0, 0, 0.4), 0px 10px 10px -5px rgba(0, 0, 0, 0.2)'
        : '0px 20px 25px -5px rgba(0, 0, 0, 0.1), 0px 10px 10px -5px rgba(0, 0, 0, 0.04)',
      isDark ? '0px 25px 50px -12px rgba(0, 0, 0, 0.5)' : '0px 25px 50px -12px rgba(0, 0, 0, 0.25)',
      ...Array(19).fill('none')
    ] as any,
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none' as const,
            borderRadius: 10,
            fontWeight: 600,
            padding: '10px 20px',
            boxShadow: 'none',
            '&:hover': {
              boxShadow: '0px 4px 12px rgba(99, 102, 241, 0.25)'
            }
          },
          contained: {
            '&:hover': {
              boxShadow: '0px 4px 12px rgba(99, 102, 241, 0.3)'
            }
          },
          outlined: {
            borderWidth: 2,
            '&:hover': {
              borderWidth: 2
            }
          },
          sizeLarge: {
            padding: '12px 28px',
            fontSize: '1rem'
          }
        }
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            boxShadow: isDark
              ? '0px 1px 3px rgba(0, 0, 0, 0.4), 0px 1px 2px rgba(0, 0, 0, 0.3)'
              : '0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06)',
            border: `1px solid ${isDark ? '#262626' : '#e2e8f0'}`,
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              boxShadow: isDark
                ? '0px 10px 15px -3px rgba(0, 0, 0, 0.4), 0px 4px 6px -2px rgba(0, 0, 0, 0.3)'
                : '0px 10px 15px -3px rgba(0, 0, 0, 0.1), 0px 4px 6px -2px rgba(0, 0, 0, 0.05)',
              transform: 'translateY(-2px)'
            }
          }
        }
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            backgroundImage: 'none'
          },
          elevation1: {
            boxShadow: isDark
              ? '0px 1px 3px rgba(0, 0, 0, 0.4), 0px 1px 2px rgba(0, 0, 0, 0.3)'
              : '0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06)'
          },
          elevation2: {
            boxShadow: isDark
              ? '0px 4px 6px -1px rgba(0, 0, 0, 0.4), 0px 2px 4px -1px rgba(0, 0, 0, 0.3)'
              : '0px 4px 6px -1px rgba(0, 0, 0, 0.1), 0px 2px 4px -1px rgba(0, 0, 0, 0.06)'
          },
          elevation3: {
            boxShadow: isDark
              ? '0px 10px 15px -3px rgba(0, 0, 0, 0.4), 0px 4px 6px -2px rgba(0, 0, 0, 0.3)'
              : '0px 10px 15px -3px rgba(0, 0, 0, 0.1), 0px 4px 6px -2px rgba(0, 0, 0, 0.05)'
          }
        }
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            boxShadow: isDark ? '0px 1px 3px rgba(0, 0, 0, 0.4)' : '0px 1px 3px rgba(0, 0, 0, 0.1)',
            backgroundColor: isDark ? '#0a0a0a' : '#ffffff',
            color: isDark ? '#f1f5f9' : '#1e293b'
          }
        }
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            borderRight: `1px solid ${isDark ? '#262626' : '#e2e8f0'}`,
            boxShadow: 'none',
            backgroundColor: isDark ? '#0a0a0a' : '#ffffff'
          }
        }
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 10,
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#6366f1'
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderWidth: 2
              }
            }
          }
        }
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            fontWeight: 600,
            fontSize: '0.813rem'
          },
          filled: {
            border: '1px solid transparent'
          }
        }
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            margin: '2px 8px',
            '&.Mui-selected': {
              backgroundColor: isDark ? 'rgba(99, 102, 241, 0.15)' : '#eef2ff',
              color: isDark ? '#a5b4fc' : '#6366f1',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: isDark ? 'rgba(99, 102, 241, 0.2)' : '#e0e7ff'
              },
              '& .MuiListItemIcon-root': {
                color: isDark ? '#a5b4fc' : '#6366f1'
              }
            },
            '&:hover': {
              backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : '#f8fafc'
            }
          }
        }
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            height: 8
          }
        }
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: 10
          },
          standardSuccess: {
            backgroundColor: isDark ? 'rgba(16, 185, 129, 0.15)' : '#d1fae5',
            color: isDark ? '#34d399' : '#065f46'
          },
          standardError: {
            backgroundColor: isDark ? 'rgba(239, 68, 68, 0.15)' : '#fee2e2',
            color: isDark ? '#f87171' : '#991b1b'
          },
          standardWarning: {
            backgroundColor: isDark ? 'rgba(245, 158, 11, 0.15)' : '#fef3c7',
            color: isDark ? '#fbbf24' : '#92400e'
          },
          standardInfo: {
            backgroundColor: isDark ? 'rgba(59, 130, 246, 0.15)' : '#dbeafe',
            color: isDark ? '#60a5fa' : '#1e40af'
          }
        }
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            backgroundColor: isDark ? '#0a0a0a' : '#ffffff',
            border: `1px solid ${isDark ? '#262626' : '#e2e8f0'}`
          }
        }
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            '&:hover': {
              backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : '#f8fafc'
            }
          }
        }
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: `1px solid ${isDark ? '#262626' : '#e2e8f0'}`
          },
          head: {
            backgroundColor: isDark ? '#000000' : '#f8fafc',
            fontWeight: 600
          }
        }
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&:hover': {
              backgroundColor: isDark ? 'rgba(255, 255, 255, 0.03)' : '#f8fafc'
            }
          }
        }
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundColor: isDark ? '#0a0a0a' : '#ffffff'
          }
        }
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none' as const,
            fontWeight: 600
          }
        }
      },
      MuiSwitch: {
        styleOverrides: {
          switchBase: {
            '&.Mui-checked': {
              color: '#6366f1',
              '& + .MuiSwitch-track': {
                backgroundColor: '#6366f1'
              }
            }
          }
        }
      }
    }
  });
};

const THEME_STORAGE_KEY = 'guardllm-theme-mode';

export const ThemeContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mode, setModeState] = useState<ThemeMode>(() => {
    const savedMode = localStorage.getItem(THEME_STORAGE_KEY);
    return (savedMode as ThemeMode) || 'dark'; // Default to dark mode
  });

  const theme = useMemo(() => getThemeConfig(mode), [mode]);

  useEffect(() => {
    localStorage.setItem(THEME_STORAGE_KEY, mode);
    // Update the document body class for CSS variable support
    document.documentElement.setAttribute('data-theme', mode);
  }, [mode]);

  const toggleTheme = () => {
    setModeState((prev) => (prev === 'light' ? 'dark' : 'light'));
  };

  const setMode = (newMode: ThemeMode) => {
    setModeState(newMode);
  };

  return (
    <ThemeContext.Provider value={{ mode, toggleTheme, setMode, theme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useThemeMode = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useThemeMode must be used within a ThemeContextProvider');
  }
  return context;
};

export default ThemeContext;
